import { useLayoutEffect, useRef, useState } from 'react';
import styles from './ProgressBorder.module.scss';

interface ProgressBorderProps {
    percent: number;
}

// Progressive export-progress border. Drop into any `position: relative`
// container: the SVG <rect> traces the container's rounded border-box and the
// purple stroke fills clockwise from the top-left (top → right → bottom → left)
// as `percent` rises 0→100.
//
// The dash is driven off the rect's MEASURED perimeter (getTotalLength), not the
// `pathLength` attribute — Chromium honors `pathLength` on <rect> inconsistently,
// which left dasharray read as raw pixels so only a stub of the stroke rendered.
// The perimeter is re-measured on resize because the label (and thus the button)
// widens as the percentage climbs.
//
// percent is a calibrated estimate (see useExportProgress), NOT live render
// progress — the server render is an opaque Lambda POST.
export function ProgressBorder({ percent }: ProgressBorderProps) {
    const pct = Math.max(0, Math.min(100, percent));
    const rectRef = useRef<SVGRectElement>(null);
    const [length, setLength] = useState(0);

    useLayoutEffect(() => {
        const rect = rectRef.current;
        if (!rect) return;
        const measure = () => {
            // getTotalLength is unimplemented in jsdom (tests) — degrade gracefully.
            try {
                setLength(rect.getTotalLength());
            } catch {
                /* no SVG geometry in this environment */
            }
        };
        measure();
        const svg = rect.ownerSVGElement;
        if (!svg || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(measure);
        observer.observe(svg);
        return () => observer.disconnect();
    }, []);

    return (
        <svg className={styles.border} aria-hidden preserveAspectRatio="none">
            <rect
                ref={rectRef}
                className={styles.fill}
                x="0"
                y="0"
                width="100%"
                height="100%"
                rx="4"
                strokeDasharray={length}
                strokeDashoffset={length * (1 - pct / 100)}
                strokeOpacity={length > 0 ? 1 : 0}
            />
        </svg>
    );
}
